import { useState } from 'react';
import { Anchor, Chip, Collapse, Group, Stack, Text } from '@mantine/core';
import {
  FeedbackTag,
  FeedbackTagGroup,
  FEEDBACK_GROUP_LABELS,
} from '../../utils/feedbackTags';

const GROUP_ORDER: FeedbackTagGroup[] = ['flavor', 'recipe'];

type FeedbackTagChipsProps = {
  tags: FeedbackTag[];
  selected: Set<string>;
  onToggle: (tagId: string) => void;
  // Group labels make sense in the rating modal's longer list; the swap
  // sheet's shorter list reads better as a single flowing group
  showGroupLabels?: boolean;
  /**
   * Hide `secondary` chips behind a "More reasons" link.
   *
   * Opt-in so the rating modal is unaffected: its chip set is different, it
   * has no attribute-rejection chips to prioritise, and there's no tap data
   * to tier it against. Only the swap sheet passes this, where the list had
   * grown to ~13 chips against a median selection of one.
   */
  collapseSecondary?: boolean;
};

/**
 * Grouped feedback-tag chip picker shared by the thumbs-down rating modal
 * (experience feedback) and the swap sheet (planning-time feedback).
 */
export const FeedbackTagChips = ({
  tags,
  selected,
  onToggle,
  showGroupLabels = true,
  collapseSecondary = false,
}: FeedbackTagChipsProps) => {
  const [expanded, setExpanded] = useState(false);

  // Split into a block that's always rendered and one that animates open.
  //
  // A secondary chip the user has TICKED gets pinned into the static block, so
  // collapsing can never hide something they're about to submit. That pinning
  // is suspended while expanded: otherwise ticking a chip would yank it out of
  // the open section and up into the static one mid-interaction. The move
  // happens once, on collapse, where it reads as part of the same gesture.
  const primaryTags = collapseSecondary
    ? tags.filter((t) => t.tier === 'primary')
    : tags;
  const secondaryTags = collapseSecondary
    ? tags.filter((t) => t.tier !== 'primary')
    : [];
  const pinnedTags = expanded
    ? []
    : secondaryTags.filter((t) => selected.has(t.id));

  // Primaries lead. Without this the flavour chips — the ones with zero
  // recorded taps — would sit at the top purely because of push order.
  const staticTags = [...primaryTags, ...pinnedTags];
  const collapsibleTags = secondaryTags.filter((t) => !pinnedTags.includes(t));

  const renderChip = (tag: FeedbackTag) => (
    <Chip
      key={tag.id}
      checked={selected.has(tag.id)}
      onChange={() => onToggle(tag.id)}
      variant="outline"
      size="sm"
    >
      {tag.label}
    </Chip>
  );

  const toggle = collapsibleTags.length > 0 && (
    <Anchor
      component="button"
      type="button"
      size="sm"
      onClick={() => setExpanded((value) => !value)}
      aria-expanded={expanded}
    >
      {expanded ? 'Fewer reasons' : `More reasons (${collapsibleTags.length})`}
    </Anchor>
  );

  const renderGroups = (groupSource: FeedbackTag[]) =>
    GROUP_ORDER.map((group) => {
      const groupTags = groupSource.filter((t) => t.group === group);
      if (groupTags.length === 0) return null;
      return (
        <Stack key={group} gap={4}>
          <Text size="xs" c="dimmed" fw={500}>
            {FEEDBACK_GROUP_LABELS[group]}
          </Text>
          <Group gap="xs">{groupTags.map(renderChip)}</Group>
        </Stack>
      );
    });

  if (!showGroupLabels) {
    return (
      <Stack gap="xs">
        <Group gap="xs">{staticTags.map(renderChip)}</Group>
        {/* Height-animated rather than mounted/unmounted, so revealing the
            long tail doesn't snap the sheet's layout. */}
        <Collapse in={expanded} transitionDuration={200}>
          <Group gap="xs">{collapsibleTags.map(renderChip)}</Group>
        </Collapse>
        {toggle}
      </Stack>
    );
  }

  return (
    <Stack gap="md">
      {renderGroups(staticTags)}
      <Collapse in={expanded} transitionDuration={200}>
        <Stack gap="md">{renderGroups(collapsibleTags)}</Stack>
      </Collapse>
      {toggle}
    </Stack>
  );
};
